import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { familyTreesAPI } from '../services/api';

interface TreeManageMembersRouteProps {
  children: React.ReactNode;
}

const isForbiddenError = (error: unknown) => {
  return typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
    (error as { response?: { status?: number } }).response?.status === 403;
};

export const TreeManageMembersRoute: React.FC<TreeManageMembersRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const { treeId } = useParams<{ treeId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [accessState, setAccessState] = useState<'unknown' | 'allowed' | 'denied' | 'error'>('unknown');

  useEffect(() => {
    let isActive = true;

    if (!treeId) {
      setIsLoading(false);
      setAccessState('denied');
      return () => {
        isActive = false;
      };
    }

    const loadAccess = async () => {
      try {
        setIsLoading(true);
        setAccessState('unknown');

        const response = await familyTreesAPI.getTreeAccess(treeId);
        const access = response.data.data ?? response.data;

        if (!isActive) {
          return;
        }

        setAccessState(Boolean(access?.canManageMembers) ? 'allowed' : 'denied');
      } catch (error) {
        if (!isActive) {
          return;
        }

        setAccessState(isForbiddenError(error) ? 'denied' : 'error');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadAccess();

    return () => {
      isActive = false;
    };
  }, [treeId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-gray-600">{t('status.loadingEllipsis', { ns: 'common' })}</p>
      </div>
    );
  }

  if (accessState === 'denied') {
    return <Navigate to="/trees" replace />;
  }

  if (!treeId || accessState === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen px-6">
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-2xl font-semibold text-gray-800">
            {t('permissions.unableToVerifyAccessTitle', { ns: 'validation' })}
          </h1>
          <p className="text-sm text-gray-600">
            {t('permissions.unableToVerifyAccessDescription', { ns: 'validation' })}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
